import { useEffect, useMemo, useState } from 'react';
import { hrApi } from '@/api/hr.api';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type { SelectOption } from '@/components/ui/Select';
import type { HrOvertimeType } from '@/types/hr';
import { showApiError } from '@/utils/apiError';

export function OvertimeTypeSelect({
  label = 'Overtime type',
  value = '',
  onChange,
  placeholder = 'Select overtime type',
  className,
  required,
  manualOnly = true,
}: {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  manualOnly?: boolean;
}) {
  const [types, setTypes] = useState<HrOvertimeType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hrApi
      .listOvertimeTypes()
      .then(setTypes)
      .catch((err) => showApiError(err, 'Failed to load overtime types'))
      .finally(() => setLoading(false));
  }, []);

  const options: SelectOption[] = useMemo(
    () =>
      types
        .filter((type) => type.active !== false)
        .filter((type) => !manualOnly || type.manualRequestAllowed !== false)
        .map((type) => ({
          value: type.code,
          label: `${type.name} (${type.code})`,
        })),
    [types, manualOnly],
  );

  return (
    <SearchableSelect
      className={className}
      label={label}
      required={required}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      loading={loading}
      searchPlaceholder="Search overtime types…"
      emptyMessage="No overtime types — configure them in HR Master data"
    />
  );
}
